<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import BaseSelect from '~/components/BaseSelect/BaseSelect'

export default {
  name: 'SubscriptionCard',

  components: {
    BaseSelect
  },

  props: {
    subscriptionProducts: {
      type: Array,
      required: true
    }
  },

  data () {
    return {
      game: '',
      durations: [
        {
          duration: 7,
          text: this.$t('nDays', { n: 7 })
        },
        {
          duration: 30,
          text: this.$t('nDays', { n: 30 })
        }
      ],
      duration: 7
    }
  },

  computed: {
    ...mapState('games', ['games']),
    ...mapState('user', ['subscriptions']),
    ...mapGetters('user', ['gameAccount', 'isAuthorized']),

    gamesList () {
      return this.games?.filter(game => this.gameAccount?.(game.name))?.map(game => ({
        value: game.id,
        title: game.extra_data?.title,
        icon: game.extra_data?.icon
      }))
    },

    btnDisabled () {
      return this.game === ''
    },

    getSubscriptionProductByDuration () {
      return this.subscriptionProducts?.find(p => p.extra_data.duration === this.duration)
    },

    getSubscriptionProductPriceByDuration () {
      return +(this.getSubscriptionProductByDuration?.price / 100).toFixed(2)
    }
  },

  methods: {
    ...mapActions('modal', ['showModal']),

    buy () {
      if (this.btnDisabled) {
        return
      }

      if (!this.isAuthorized) {
        this.showModal({
          component: 'Auth',
          data: {
            mode: 'register',
            afterLogin: this.buy
          }
        })

        return
      }

      // this.$gtag.event('buy_subscription_click', { product_id: this.getSubscriptionProductByDuration.id })

      return this.showModal({
        component: 'ModalCheckout',
        data: {
          product: this.getSubscriptionProductByDuration,
          gameId: this.game
        }
      })
    }
  }
}
</script>

<template>
  <div :class="$style.card">
    <div :class="$style.card__picture">
      <p :class="$style.card__title">
        {{ $t("startWinningRealMoney") }}
      </p>
    </div>

    <div :class="$style.settings">
      <p :class="$style.settings__header">
        {{ $t("game") }}
      </p>

      <BaseSelect
        v-model="game"
        :class="$style.selector"
        :options="gamesList"
        :with-empty-value="false"
        :placeholder="$t('chooseAGame')"
        with-title
        light
      />

      <p :class="$style.settings__header">
        {{ $t("duration") }}
      </p>

      <div :class="$style.durations">
        <div v-for="d in durations" :key="d.duration" :class="{ [$style.duration]: true, [$style.durationActive]: d.duration === duration }" @click="duration = d.duration">
          {{ d.text }}
        </div>
      </div>

      <div :class="$style.spaceBetween">
        <span :class="$style.settings__header">{{ $t("price") }}</span>
        <Currency real type="money" size="16">
          <span :class="$style.settings__price">{{ getSubscriptionProductPriceByDuration }}</span>
        </Currency>
      </div>
    </div>

    <div :class="$style.notification__container">
      <p :class="$style.notification">
        {{ $t("autoTournamentRegistrationNotification") }}
      </p>
    </div>

    <Button :disabled="btnDisabled" :class="$style.card__button" is-primary @click="buy">
      {{ $t('buy') }}
    </Button>
  </div>
</template>

<style lang="scss" module>
.card {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: $blue-grey70;
  color: $white;

  &__title {
    font-size: 18px;
    font-weight: 700;
    margin: 12px 0;

    @media(min-width: $breakpointTablet) {
      font-size: 20px;
    }
  }

  &__picture {
    position: relative;
    width: 100%;
    padding: 24px 32px;
    margin-bottom: 20px;
    border: 1px solid $light-blue60;
    border-radius: 16px;
    background-image: url(~@/assets/img/backgrounds/unlimited.png);
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;

    @media (min-width: $breakpointTablet) {
      background-image: url(~@/assets/img/backgrounds/unlimited_big.png);
      background-size: auto;
      background-position: right center;
    }

    @media (min-width: $breakpointDesktopBig) {
      background-image: url(~@/assets/img/backgrounds/unlimited_big.png);
      background-size: auto;
      background-position: right center;
    }
  }

  &__button {
    width: 100%;
    min-width: 100% !important;
    height: 48px;
    margin-top: 24px;

    &:disabled {
      opacity: 0.4;
      cursor: none;
    }
  }
}

.selector {
  margin: 8px 0 24px;
}

.settings {
  padding: 16px;
  background: $blue-grey60;
  border-radius: 16px;

  &__header {
    margin: 0;
    color: $white;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
  }

  &__price {
    color: $white;
    font-size: 18px;
    font-weight: 700;
    line-height: 30px;
  }
}

.durations {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  align-items: center;
  margin: 8px 0 16px;
  padding: 0 0 16px;
  border-bottom: 1px solid $blue-grey40;
}

.duration {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background: $blue-grey30;
  color: $white;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  border-radius: 100px;

  &Active {
    background: $primary60;
  }
}

.spaceBetween {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notification {
  margin: 0;
  color: $amber60;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.16px;
  text-align: center;

  &__container {
    padding: 16px;
    margin: 20px 0 24px;
    background: $blue-grey60;
    border-radius: 16px;
  }
}
</style>
